import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  updateTaskSchema, IMPORTANT_CHAIN_TOO_SHORT, MIN_IMPORTANT_ASSIGNEES, PERMISSIONS,
  type UpdateTaskInput, type TaskDetail, type PriorityLimitDetails,
} from '@pms/shared';
import { Pencil, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { TimePicker } from '@/components/ui/time-picker';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { usePermission } from '@/hooks/usePermission';
import { tasksService } from '@/services/tasks.service';
import { getErrorMessage, getPriorityLimitDetails } from '@/lib/errors';
import { VoiceNoteRecorder, type VoiceNoteDraft } from './VoiceNoteRecorder';
import { PriorityLimitDialog } from './PriorityLimitDialog';
import { VoiceNotePlayer } from './VoiceNotePlayer';
import { AssigneePicker } from './AssigneePicker';
import { AssigneeSequenceList } from './AssigneeSequenceList';

/**
 * Shown while Important is OFF and the roster is too short to turn it on. The shared
 * IMPORTANT_CHAIN_TOO_SHORT ends with "…or turn off Important", which only makes sense once
 * it is already on — that message still covers the ticked-but-short case further down.
 */
const IMPORTANT_NEEDS_MORE_PEOPLE =
  `Pick at least ${MIN_IMPORTANT_ASSIGNEES} people below to make this an Important task.`;

/**
 * Small caps section label. Same treatment VoiceNoteRecorder already uses for its own
 * heading, so the groups introduced here read as part of the existing form rather than a
 * new device.
 */
function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </p>
  );
}

interface EditTaskModalProps {
  task: TaskDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function EditTaskModal({ task, open, onOpenChange, onSaved }: EditTaskModalProps) {
  const qc = useQueryClient();
  const { has } = usePermission();

  // Postgres TIME columns arrive as "09:00:00", but both TimePicker and
  // timeOfDaySchema want bare HH:MM — without the slice the picker reads the value
  // as unset and shows its placeholder, and saving fails validation.
  const formValues = useMemo<UpdateTaskInput>(() => ({
    title: task.title,
    description: task.description ?? '',
    is_priority: task.is_priority,
    reminder_enabled: task.reminder_enabled,
    is_important: task.is_important,
    start_time: task.start_time?.slice(0, 5) ?? '',
    end_time: task.end_time?.slice(0, 5) ?? '',
  }), [task]);

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<UpdateTaskInput>({
    resolver: zodResolver(updateTaskSchema),
    defaultValues: formValues,
  });

  // A freshly recorded note replacing whatever the task has; `removeVoiceNote`
  // covers deleting the existing one without recording a new one.
  const [voiceNote, setVoiceNote] = useState<VoiceNoteDraft | null>(null);
  const [removeVoiceNote, setRemoveVoiceNote] = useState(false);
  const [priorityLimit, setPriorityLimit] = useState<PriorityLimitDetails | null>(null);
  const hasStoredNote = !!task.voice_note_path;

  // The roster, as an ordered list of ids — position 0 is the first person an important
  // task goes to. `task.assignees` already arrives ordered by sequence_order.
  const currentIds = useMemo(() => task.assignees.map((a) => a.user_id), [task]);
  const [assignees, setAssignees] = useState<string[]>(currentIds);

  // The three assignee endpoints require task.assign, while this modal opens for the task
  // CREATOR. The two are unrelated, so a creator without it would see the picker and get a
  // 403 on save — hide the section instead; every other field still edits fine.
  const canAssign = has(PERMISSIONS.TASK_ASSIGN);

  // Once someone starts work the holder is frozen for that occurrence, so reordering the
  // chain would not move it. The drawer hides its arrows for the same reason.
  const chainLocked = task.occurrence_assignees.some((r) => r.holder_locked_at);

  // useWatch rather than watch(): the latter returns a fresh function each render, which
  // stops the React Compiler memoising this component.
  const isImportant = useWatch({ control, name: 'is_important' }) ?? task.is_important;
  // Measured against the LIVE selection, not task.assignees: the point of having the picker
  // here is that you can tick Important and add the second person in one visit.
  const chainTooShort = !!isImportant && canAssign && assignees.length < MIN_IMPORTANT_ASSIGNEES;
  // Every task keeps at least one person — the server refuses to remove the last one.
  const noAssignee = canAssign && assignees.length === 0;
  // Gated on the LIVE tick, not task.is_important: only turning Important ON needs the
  // minimum. Anything already on can always be turned off — including a tick made in this
  // session whose chain has since been emptied. Keying this to the saved value trapped the
  // user, since the disabled box kept is_important true and Save stayed blocked too.
  const canToggleImportant = !!isImportant
    || (canAssign ? assignees.length : task.assignees.length) >= MIN_IMPORTANT_ASSIGNEES;

  // The assignee list needs no reset here — the caller mounts this only while open, so
  // useState above re-seeds it from the task on every opening.
  useEffect(() => {
    if (open) reset(formValues);
  }, [open, formValues, reset]);

  /** Drops any unsaved recording. Called from the close paths, never from an effect. */
  function clearVoiceDraft() {
    setVoiceNote(null);
    setRemoveVoiceNote(false);
  }

  const save = useMutation({
    mutationFn: async (data: UpdateTaskInput) => {
      const payload: UpdateTaskInput = {
        ...data,
        description: data.description === '' ? null : data.description,
        start_time: data.start_time === '' ? null : data.start_time,
        end_time: data.end_time === '' ? null : data.end_time,
      };

      // ── Order matters, and every position is forced by a server rule ──────────────
      // These are four separate calls with no shared transaction.
      //
      //   adds  →  PATCH  →  removes  →  reorder
      //
      // Adds BEFORE the PATCH: turning Important on while adding the 2nd person is the
      // whole reason this picker exists, and the PATCH counts the roster as it stands.
      // Removes AFTER it: the mirror case, turning Important off while dropping to one —
      // removeAssignee reads the task's live is_important. Reorder LAST: it rejects any
      // list that is not an exact permutation of the roster at that moment.
      const add = canAssign ? assignees.filter((id) => !currentIds.includes(id)) : [];
      const remove = canAssign ? currentIds.filter((id) => !assignees.includes(id)) : [];

      if (add.length) await tasksService.addAssignees(task.id, { assignee_ids: add });

      await tasksService.update(task.id, payload);

      for (const userId of remove) await tasksService.removeAssignee(task.id, userId);

      // Only when the final order actually differs, and never on a locked chain — the
      // holder is frozen once work starts, so a reorder there would not move it anyway.
      const orderChanged = canAssign
        && assignees.length === currentIds.length
        && assignees.some((id, i) => id !== currentIds[i]);
      if (canAssign && (add.length || remove.length || orderChanged) && !chainLocked) {
        await tasksService.reorderAssignees(task.id, { assignee_ids: assignees });
      }

      // As on create: the task edit already succeeded, so a voice-note failure is
      // reported as a warning rather than making the whole save look failed.
      try {
        if (voiceNote) {
          await tasksService.uploadVoiceNote(task.id, voiceNote.blob, voiceNote.durationMs);
        } else if (removeVoiceNote && hasStoredNote) {
          await tasksService.deleteVoiceNote(task.id);
        }
      } catch (e) {
        return { voiceNoteError: getErrorMessage(e) };
      }
      return { voiceNoteError: null as string | null };
    },
    onSuccess: (result) => {
      if (result.voiceNoteError) {
        toast.warning(`Task updated, but the voice note change failed. (${result.voiceNoteError})`);
      } else {
        toast.success('Task updated');
      }
      qc.invalidateQueries({ queryKey: ['tasks'] });
      clearVoiceDraft();
      onSaved();
      onOpenChange(false);
    },
    // Turning an existing task Priority can breach the per-person cap; that gets the
    // dialog listing the blocking tasks, everything else stays a toast.
    //
    // onSaved() runs here too: the save is four independent calls, so a failure part-way
    // through leaves the earlier ones committed. Without refetching, the drawer behind
    // this modal would keep showing a roster that no longer matches the server.
    onError: (e) => {
      const limit = getPriorityLimitDetails(e);
      if (limit) setPriorityLimit(limit);
      else toast.error(getErrorMessage(e));
      onSaved();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) clearVoiceDraft(); onOpenChange(o); }}>
      {/* Sticky header and footer with only the middle scrolling — the ApplyLeaveModal
          pattern. DialogContent is fixed and centred by transform, so without a height cap
          a tall form is clipped at BOTH ends: the X goes off the top and Save off the
          bottom, with no way to scroll to either. Capping alone (as CreateTaskModal does)
          would scroll Save away too, which is worst exactly when the chain is long. */}
      {/* Only widens when there is a second column to put there. Without task.assign the
          People side is hidden, and a 3xl modal would leave one very wide column of
          fields. */}
      <DialogContent className={cn(
        'flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-hidden p-0',
        canAssign && 'md:max-w-3xl',
      )}>
        {/* pr-10 keeps the title clear of DialogContent's built-in close button, which is
            absolutely positioned at right-4 and no longer has the old p-6 to sit in. */}
        <DialogHeader className="shrink-0 border-b border-border/60 px-5 py-3 pr-10">
          <DialogTitle className="flex items-center gap-2.5">
            {/* The teal icon-in-box from ui/section-header — the app's standard way of
                titling a section. Inlined rather than pulling SectionHeader in, since that
                one renders a CardHeader. */}
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Pencil className="h-3.5 w-3.5" />
            </span>
            Edit Task
          </DialogTitle>
        </DialogHeader>

        {/* The form wraps body + footer so the submit button still belongs to it. */}
        <form
          onSubmit={handleSubmit((d) => save.mutate(d))}
          className="flex min-h-0 flex-1 flex-col"
        >
          {/* ── Scrolling model ──────────────────────────────────────────────────────
              Below md this is the ONE scroller and the columns just stack.
              From md up it stops scrolling and hands that job to each column, because a
              capped list inside a scrolling parent puts two scrollbars side by side —
              which is exactly what a long assignee chain used to produce here. The People
              column then never scrolls as a column either: its two lists are flex siblings
              that scroll independently, so no scroller is ever nested in another. */}
          <div className={cn(
            'min-h-0 flex-1 overflow-y-auto md:overflow-hidden',
            canAssign && 'md:grid md:grid-cols-2',
          )}>
          <div className="thin-scrollbar space-y-5 px-5 py-4 md:h-full md:min-h-0 md:overflow-y-auto">
          <div className="space-y-3">
            <Eyebrow>Details</Eyebrow>
            <div className="space-y-1.5">
              <Label>
                Title <span className="text-rose-500">*</span>
              </Label>
              <Input {...register('title')} />
              {errors.title && <p className="text-xs text-rose-500">{errors.title.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                {...register('description')}
                rows={3}
                maxLength={2000}
                placeholder="Optional description…"
                className="resize-none"
              />
            </div>
          </div>

          <VoiceNoteRecorder
            value={voiceNote}
            onChange={(draft) => {
              setVoiceNote(draft);
              // Recording a replacement cancels a pending removal.
              if (draft) setRemoveVoiceNote(false);
            }}
            disabled={save.isPending}
            existing={hasStoredNote && !removeVoiceNote ? (
              <div className="space-y-2">
                <VoiceNotePlayer src={`/tasks/${task.id}/voice-note`} />
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={() => setRemoveVoiceNote(true)}
                  disabled={save.isPending}
                >
                  Remove current voice note
                </Button>
              </div>
            ) : hasStoredNote && removeVoiceNote ? (
              <p className="text-xs text-muted-foreground">
                The current voice note will be removed when you save.{' '}
                <button
                  type="button"
                  className="text-primary underline"
                  onClick={() => setRemoveVoiceNote(false)}
                >
                  Keep it
                </button>
              </p>
            ) : null}
          />

          <div className="space-y-3">
            <Eyebrow>Schedule</Eyebrow>
            {/* Stacks on a phone, two columns from sm: up — the project rule. */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Start Time</Label>
                <Controller
                  control={control}
                  name="start_time"
                  render={({ field }) => (
                    // `modal` is required inside a Dialog — without it the popover
                    // self-dismisses on touch. See the note in ui/time-picker.
                    <TimePicker
                      modal
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      placeholder="Pick start time"
                    />
                  )}
                />
              </div>
              <div className="space-y-1.5">
                <Label>End Time</Label>
                <Controller
                  control={control}
                  name="end_time"
                  render={({ field }) => (
                    <TimePicker
                      modal
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      placeholder="Pick end time"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* One panel of full-width rows rather than a wrapping line of bare checkboxes:
              each row is its own hit target and the labels stop re-flowing at odd widths. */}
          <div className="space-y-3">
            <Eyebrow>Options</Eyebrow>
            <div className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border/60 bg-card">
              {/* A daily task cannot be a priority task. The type is fixed once created,
                  so this hides for good rather than toggling as a selection changes. */}
              {task.task_type !== 'daily' && (
                <label className="flex min-h-[44px] cursor-pointer items-center justify-between gap-3 px-3 py-2.5 text-sm transition-colors hover:bg-muted/40">
                  <span className="font-medium">Priority</span>
                  <Checkbox {...register('is_priority')} className="accent-primary" />
                </label>
              )}
              <label className="flex min-h-[44px] cursor-pointer items-center justify-between gap-3 px-3 py-2.5 text-sm transition-colors hover:bg-muted/40">
                <span className="font-medium">Reminder</span>
                <Checkbox {...register('reminder_enabled')} className="accent-primary" />
              </label>
              {/* Only ever disabled while OFF with too short a chain — turning it on would be
                  rejected by the server. Turning it off is always allowed. */}
              <label
                className={cn(
                  'flex min-h-[44px] items-center justify-between gap-3 px-3 py-2.5 text-sm transition-colors',
                  canToggleImportant
                    ? 'cursor-pointer hover:bg-muted/40'
                    : 'cursor-not-allowed opacity-60',
                )}
                title={canToggleImportant ? undefined : IMPORTANT_NEEDS_MORE_PEOPLE}
              >
                <span className="min-w-0">
                  <span className="font-medium">Important (sequential)</span>
                  {!canToggleImportant && (
                    <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                      {IMPORTANT_NEEDS_MORE_PEOPLE}
                    </span>
                  )}
                </span>
                <Checkbox
                  {...register('is_important')}
                  disabled={!canToggleImportant}
                  className="accent-primary"
                />
              </label>
            </div>
          </div>

          </div>

          {/* Hidden without task.assign — this modal opens for the task creator, which is
              a different gate from the one the assignee endpoints enforce. */}
          {canAssign && (
            <div className="flex min-h-0 flex-col gap-3 border-t border-border/60 px-5 py-4 md:h-full md:border-l md:border-t-0">
              <div className="flex shrink-0 items-center justify-between gap-2">
                <Eyebrow>{isImportant ? 'Assignment Sequence' : 'Assign To'}</Eyebrow>
                {assignees.length > 0 && (
                  <Badge variant="secondary">
                    {assignees.length} selected
                  </Badge>
                )}
              </div>

              {/* Sequence and picker are siblings, each scrolling itself from md: up. The
                  sequence gets the smaller share — the picker is the one you hunt in. */}
              {isImportant && (
                <AssigneeSequenceList
                  selected={assignees}
                  onChange={setAssignees}
                  disabled={save.isPending}
                  // No `shrink-0` here: it and `md:flex-1` both set flex-shrink, and
                  // Tailwind emits shrink-* after flex-*, so it would win at md: too.
                  className="thin-scrollbar max-h-44 overflow-y-auto md:max-h-none md:min-h-0 md:flex-1"
                />
              )}
              <AssigneePicker
                selected={assignees}
                onChange={setAssignees}
                showSelectedCount={false}
                className="md:min-h-0 md:flex-[1.4]"
                // Capped like everywhere else while the page stacks; from md: up the cap
                // comes off and the list fills its column, so the picker is never a
                // scroller inside another scroller.
                listClassName="max-h-52 md:max-h-none"
              />
              {noAssignee && (
                <p className="shrink-0 text-xs text-rose-500">A task needs at least one assignee.</p>
              )}
              {chainTooShort && !noAssignee && (
                <p className="shrink-0 text-xs text-rose-500">{IMPORTANT_CHAIN_TOO_SHORT}</p>
              )}
              {isImportant && chainLocked && (
                <p className="shrink-0 text-xs text-muted-foreground">
                  Work has started on the current occurrence, so the order is frozen until
                  the next one.
                </p>
              )}
            </div>
          )}
          </div>

          <DialogFooter className="shrink-0 border-t border-border/60 px-5 py-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {/* Blocked client-side too: removals run one call at a time, so a server
                rejection part-way through would leave the earlier ones committed. */}
            <Button type="submit" disabled={save.isPending || chainTooShort || noAssignee}>
              <Save className="h-4 w-4" />
              {save.isPending ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <PriorityLimitDialog details={priorityLimit} onClose={() => setPriorityLimit(null)} />
    </Dialog>
  );
}
